import {
  Flex,
  HitCounter,
  Loading,
  Table,
  TablePageSizeSelector,
  TableRowMenuCell,
  Text,
} from '@/components';
import { TemplateAbilityGuard } from '@/features/authentication/_components/TemplateAbilityGuard';
import { TABLE_ROW_MENU_WIDTH } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationTemplatesWithPagination } from '../_hooks/organization-template';
import { useOrganizationTemplatePageSearchQueryStates } from '../_utils/search-query-states';
import { OrganizationTemplateDeleteModal } from './OrganizationTemplateDeleteModal';
import { useOrganizationTemplateDrawerState } from './OrganizationTemplateDrawerStateProvider';

export const OrganizationTemplateListTable: FC = () => {
  const { t } = useTranslation();
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const { setDrawerState } = useOrganizationTemplateDrawerState();
  const { searchQueryStates, setSearchQueryStates } =
    useOrganizationTemplatePageSearchQueryStates();

  const templates = useOrganizationTemplatesWithPagination({
    page: searchQueryStates.page,
    pageSize: searchQueryStates.pageSize,
    cascader: searchQueryStates.cascader,
    q: searchQueryStates.q,
  });

  if (templates.isPending) {
    return <Loading />;
  }

  return (
    <>
      <Flex vertical gap={16}>
        <Flex justify="space-between" gap={8} align="center">
          <HitCounter
            count={templates.data?.count ?? 0}
            label={t('relevant')}
          />
          <Flex gap={8} align="center">
            <TablePageSizeSelector
              defaultPageSize={searchQueryStates.pageSize}
              onChange={(newPageSize) => {
                setSearchQueryStates((prev) => {
                  return {
                    ...prev,
                    page: 1,
                    pageSize: newPageSize,
                  };
                });
              }}
            />
          </Flex>
        </Flex>
        <Table
          dataSource={templates.data?.results}
          rowKey="id"
          columns={[
            {
              title: t('organization-template.name'),
              key: 'name',
              dataIndex: 'name',
              width: 300,
            },
            {
              title: t('organization-template.user-organizations'),
              key: 'userOrganizations',
              width: 200,
              render: (_, { userOrganizations }) => (
                <Text
                  weight="regular"
                  variant="body"
                  maxRows={3}
                  ellipsis={{
                    tooltip: {
                      styles: {
                        root: {
                          maxHeight: 400,
                          overflow: 'scroll',
                          whiteSpace: 'pre-line',
                        },
                      },
                    },
                  }}
                  text={
                    userOrganizations.length
                      ? userOrganizations
                          .map((org) => org.organizationName)
                          .join('\n')
                      : null
                  }
                />
              ),
            },
            {
              title: t('organization-template.template-organizations'),
              key: 'organizations',
              width: 300,
              render: (_, { organizations }) => (
                <Text
                  weight="regular"
                  variant="body"
                  maxRows={3}
                  ellipsis={{
                    tooltip: {
                      styles: {
                        root: {
                          maxHeight: 400,
                          overflow: 'scroll',
                          whiteSpace: 'pre-line',
                        },
                      },
                    },
                  }}
                  text={
                    organizations.length
                      ? organizations
                          .map((org) => org.organizationName)
                          .join('\n')
                      : null
                  }
                />
              ),
            },
            {
              key: 'actions',
              fixed: 'right' as const,
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TemplateAbilityGuard>
                  <TableRowMenuCell
                    onEdit={() =>
                      setDrawerState({
                        open: true,
                        operation: 'update',
                        templateId: record.id,
                      })
                    }
                    items={[
                      {
                        key: 'delete',
                        label: t('delete'),
                        danger: true,
                        onClick: () => setDeleteTemplateId(record.id),
                      },
                    ]}
                  />
                </TemplateAbilityGuard>
              ),
            },
          ]}
          pagination={{
            current: searchQueryStates.page,
            total: templates.data?.count,
            showSizeChanger: false,
            position: ['bottomRight'],
            pageSize: searchQueryStates.pageSize,
          }}
          onChange={(pagination, _, __, extra) => {
            switch (extra.action) {
              case 'paginate':
                if (pagination.current) {
                  setSearchQueryStates((prev) => ({
                    ...prev,
                    page: pagination.current,
                  }));
                }
                return;
              default: {
                return;
              }
            }
          }}
        />
      </Flex>
      <OrganizationTemplateDeleteModal
        deleteTemplateId={deleteTemplateId}
        onClose={() => setDeleteTemplateId(null)}
      />
    </>
  );
};
